import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Business,
  LocationOn,
  Email,
  Phone,
  Star,
  Warning,
  Schedule,
  Pause,
  PlayArrow,
  Block,
  Home,
  Pets,
  VerifiedUser,
  Image as ImageIcon,
  History,
  Cake,
  LocalOffer,
  ExpandMore,
  ExpandLess,
  Person,
  Language,
} from '@mui/icons-material';
import { useAdminAuth } from '../context/AdminAuthContext';
import { PendingProfile } from '../types/admin';
import CircularProgress from '@mui/material/CircularProgress';

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

// Helper function to map backend profile to frontend format
const mapBackendProfileToFrontend = (backendProfile: any): PendingProfile => {
  return {
    id: backendProfile._id || backendProfile.id,
    submittedAt: new Date(backendProfile.submittedAt),
    serviceType: backendProfile.serviceType || 'sitter',
    personalInfo: {
      firstName: backendProfile.firstName,
      lastName: backendProfile.lastName,
      email: backendProfile.email,
      phoneNumber: backendProfile.phone,
      address: `${backendProfile.address.street}, ${backendProfile.address.city}, ${backendProfile.address.state} ${backendProfile.address.zipCode}`,
      country: backendProfile.address.country || 'USA',
      dateOfBirth: backendProfile.dateOfBirth || null,
      yearsOfExperience: backendProfile.experienceYears || 0,
    },
    professionalInfo: {
      qualification: backendProfile.qualifications,
      veterinarianLicense: backendProfile.professionalLicenses?.[0],
    },
    sitterInfo: backendProfile.sitterInfo ? {
      homeType: backendProfile.sitterInfo.homeType,
      petsWelcome: backendProfile.sitterInfo.petsWelcome || [],
      skills: backendProfile.sitterInfo.skills || [],
      aboutMe: backendProfile.sitterInfo.aboutMe,
      profileImage: backendProfile.sitterInfo.profileImage,
      homeImages: backendProfile.sitterInfo.homeImages || [],
    } : undefined,
    groomerInfo: backendProfile.groomerInfo ? {
      businessName: backendProfile.groomerInfo.businessName,
      groomingType: backendProfile.groomerInfo.groomingType,
      registeredBusiness: backendProfile.groomerInfo.registeredBusiness,
      taxNumber: backendProfile.groomerInfo.taxNumber,
      professionalTraining: backendProfile.groomerInfo.professionalTraining,
      trainingDetails: backendProfile.groomerInfo.trainingDetails,
      liabilityInsurance: backendProfile.groomerInfo.liabilityInsurance,
      insuredPetInjury: backendProfile.groomerInfo.insuredPetInjury,
      insuredPetEscape: backendProfile.groomerInfo.insuredPetEscape,
      insuredPropertyDamage: backendProfile.groomerInfo.insuredPropertyDamage,
      insuranceProof: backendProfile.groomerInfo.insuranceProof,
      safetyPractices: backendProfile.groomerInfo.safetyPractices,
      aboutMe: backendProfile.groomerInfo.aboutMe,
      petsWelcome: backendProfile.groomerInfo.petsWelcome || [],
      skills: backendProfile.groomerInfo.skills || [],
      profileImage: backendProfile.groomerInfo.profileImage,
      businessImages: backendProfile.groomerInfo.businessImages || [],
    } : undefined,
    vetInfo: backendProfile.vetInfo ? {
      clinicName: backendProfile.vetInfo.clinicName,
      isLicensed: backendProfile.vetInfo.isLicensed,
      licenseCountry: backendProfile.vetInfo.licenseCountry,
      licenseNumber: backendProfile.vetInfo.licenseNumber,
      qualificationProof: backendProfile.vetInfo.qualificationProof,
      aboutMe: backendProfile.vetInfo.aboutMe,
      petsWelcome: backendProfile.vetInfo.petsWelcome || [],
      skills: backendProfile.vetInfo.skills || [],
      profileImage: backendProfile.vetInfo.profileImage,
      clinicImages: backendProfile.vetInfo.clinicImages || [],
    } : undefined,
    trainerInfo: backendProfile.trainerInfo ? {
      isCertified: backendProfile.trainerInfo.isCertified,
      certificationDetails: backendProfile.trainerInfo.certificationDetails,
      certificationProof: backendProfile.trainerInfo.certificationProof,
      aboutMe: backendProfile.trainerInfo.aboutMe,
      petsWelcome: backendProfile.trainerInfo.petsWelcome || [],
      skills: backendProfile.trainerInfo.skills || [],
      profileImage: backendProfile.trainerInfo.profileImage,
      facilityImages: backendProfile.trainerInfo.facilityImages || [],
    } : undefined,
    spokenLanguages: backendProfile.spokenLanguages || [],
    services: (backendProfile.services || []).map((service: any) => ({
      _id: service._id,
      name: service.name,
      price: service.price,
      pricingType: service.pricingType,
      currency: service.currency,
    })),
    documents: backendProfile.documents ? {
      idDocument: backendProfile.documents.find((d: any) => d.type === 'id')?.url,
      licenseDocument: backendProfile.documents.find((d: any) => d.type === 'license')?.url,
      qualificationCertificate: backendProfile.documents.find((d: any) => d.type === 'certificate')?.url,
    } : undefined,
    status: backendProfile.status,
    urgency: backendProfile.urgency || 'normal',
    language: backendProfile.language,
    rejectionReason: backendProfile.statusReason,
    platformFeeOverridePercent: backendProfile.platformFeeOverridePercent ?? null,
    feeExemptUntil: backendProfile.feeExemptUntil ? new Date(backendProfile.feeExemptUntil) : null,
    feeOverrideSetBy: backendProfile.feeOverrideSetBy || null,
    feeOverrideSetAt: backendProfile.feeOverrideSetAt ? new Date(backendProfile.feeOverrideSetAt) : null,
    timesApplied: backendProfile.timesApplied || 1,
    timesRejected: backendProfile.timesRejected || 0,
    timesSuspended: backendProfile.timesSuspended || 0,
    timesReactivated: backendProfile.timesReactivated || 0,
    statusHistory: (backendProfile.statusHistory || []).map((entry: any) => ({
      action: entry.action,
      performedBy: entry.performedBy || { name: 'Unknown' },
      reason: entry.reason,
      timestamp: new Date(entry.timestamp),
    })),
  };
};

interface ProfileApprovalListProps {
  statusFilter?: 'pending' | 'approved' | 'rejected' | 'suspended';
  openProfileId?: string | null;
  onProfileOpened?: () => void;
}

const ProfileApprovalList: React.FC<ProfileApprovalListProps> = ({ statusFilter = 'pending', openProfileId, onProfileOpened }) => {
  const { logActivity } = useAdminAuth();
  const [profiles, setProfiles] = useState<PendingProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<PendingProfile | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [discountActivity, setDiscountActivity] = useState<any[]>([]);
  const [discountActivityLoading, setDiscountActivityLoading] = useState(false);
  const [providerDiscountCodes, setProviderDiscountCodes] = useState<any[]>([]);
  const [discountCodesLoading, setDiscountCodesLoading] = useState(false);
  const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);
  const [codeDetails, setCodeDetails] = useState<Record<string, any>>({});
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalMessage, setApprovalMessage] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    street: '', city: '', state: '', zipCode: '',
    services: [] as Array<{ _id: string; name: string; price: number; currency: string; pricingType: string }>,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [feeOverridePercent, setFeeOverridePercent] = useState<string>('');
  const [feeExemptUntil, setFeeExemptUntil] = useState<string>('');
  const [feeSaving, setFeeSaving] = useState(false);

  const openEditDialog = () => {
    if (!selectedProfile) return;
    const addr = selectedProfile.personalInfo.address || '';
    const parts = addr.split(',').map(s => s.trim());
    setEditForm({
      firstName: selectedProfile.personalInfo.firstName || '',
      lastName: selectedProfile.personalInfo.lastName || '',
      email: selectedProfile.personalInfo.email || '',
      phone: selectedProfile.personalInfo.phoneNumber || '',
      street: parts[0] || '',
      city: parts[1] || '',
      state: parts[2] || '',
      zipCode: parts[3] || '',
      services: selectedProfile.services.map((s: any) => ({
        _id: s._id || s.id || '',
        name: s.name,
        price: s.price,
        currency: s.currency || 'USD',
        pricingType: s.pricingType,
      })),
    });
    setEditDialogOpen(true);
  };

  const saveEditDetails = async () => {
    if (!selectedProfile) return;
    setEditSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/profiles/${selectedProfile.id}/details`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          email: editForm.email,
          phone: editForm.phone,
          address: {
            street: editForm.street,
            city: editForm.city,
            state: editForm.state,
            zipCode: editForm.zipCode,
          },
          services: editForm.services,
        }),
      });
      if (response.ok) {
        alert('Profile updated successfully');
        setEditDialogOpen(false);
        fetchProfiles();
      } else {
        const data = await response.json();
        alert(`${data.error || 'Failed to update profile'}${data.details ? ': ' + data.details : ''}`);
      }
    } catch (err) {
      alert('Error updating profile');
    } finally {
      setEditSaving(false);
    }
  };

  const initFeeOverrideForm = (profile: PendingProfile) => {
    setFeeOverridePercent(
      profile.platformFeeOverridePercent != null ? String(profile.platformFeeOverridePercent) : ''
    );
    setFeeExemptUntil(
      profile.feeExemptUntil ? new Date(profile.feeExemptUntil).toISOString().split('T')[0] : ''
    );
  };

  const saveFeeOverride = async () => {
    if (!selectedProfile) return;
    setFeeSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/profiles/${selectedProfile.id}/fee-override`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          platformFeeOverridePercent: feeOverridePercent !== '' ? Number(feeOverridePercent) : null,
          feeExemptUntil: feeExemptUntil || null,
        }),
      });
      if (response.ok) {
        alert('Fee override updated successfully');
        fetchProfiles();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update fee override');
      }
    } catch (err) {
      alert('Error updating fee override');
    } finally {
      setFeeSaving(false);
    }
  };

  const resetFeeOverride = async () => {
    if (!selectedProfile) return;
    if (!window.confirm('Reset this provider to the default 17% platform fee?')) return;
    setFeeSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/profiles/${selectedProfile.id}/fee-override`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          platformFeeOverridePercent: null,
          feeExemptUntil: null,
        }),
      });
      if (response.ok) {
        setFeeOverridePercent('');
        setFeeExemptUntil('');
        alert('Fee override removed — default 17% fee restored');
        fetchProfiles();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to reset fee override');
      }
    } catch (err) {
      alert('Error resetting fee override');
    } finally {
      setFeeSaving(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [statusFilter]);

  // Auto-open a specific profile when requested (e.g. from notification click)
  useEffect(() => {
    if (openProfileId && profiles.length > 0) {
      const profile = profiles.find(p => p.id === openProfileId);
      if (profile) {
        setSelectedProfile(profile);
        setDialogOpen(true);
        onProfileOpened?.();
      }
    }
  }, [openProfileId, profiles]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const endpoint = statusFilter === 'pending'
        ? `${API_BASE_URL}/api/admin/profiles/pending`
        : `${API_BASE_URL}/api/admin/profiles?status=${statusFilter}`;
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // The /pending endpoint returns an array, the / endpoint returns { profiles, pagination }
        const rawProfiles = Array.isArray(data) ? data : (data.profiles || []);
        const mappedProfiles = rawProfiles.map(mapBackendProfileToFrontend);
        setProfiles(mappedProfiles);
      } else {
        throw new Error('Failed to fetch profiles');
      }
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const getServiceTypeInfo = (type: string) => {
    const types = {
      sitter: { label: 'Pet Sitter', color: 'primary', icon: '🐕' },
      groomer: { label: 'Pet Groomer', color: 'secondary', icon: '✂️' },
      vet: { label: 'Veterinarian', color: 'success', icon: '🏥' },
      trainer: { label: 'Pet Trainer', color: 'info', icon: '🎾' },
    };
    return types[type as keyof typeof types] || types.sitter;
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'error';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const fetchDiscountActivity = async (profileId: string) => {
    try {
      setDiscountActivityLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/discount-notifications/${profileId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDiscountActivity(data.notifications || []);
      } else {
        setDiscountActivity([]);
      }
    } catch {
      setDiscountActivity([]);
    } finally {
      setDiscountActivityLoading(false);
    }
  };

  const fetchProviderDiscountCodes = async (profileId: string) => {
    try {
      setDiscountCodesLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/discount-codes?providerId=${profileId}&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProviderDiscountCodes(data.discountCodes || []);
      } else {
        setProviderDiscountCodes([]);
      }
    } catch {
      setProviderDiscountCodes([]);
    } finally {
      setDiscountCodesLoading(false);
    }
  };

  const fetchCodeDetail = async (codeId: string) => {
    if (codeDetails[codeId]) return; // Already fetched
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/discount-codes/${codeId}/detail`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCodeDetails(prev => ({ ...prev, [codeId]: data }));
      }
    } catch {
      // Silently fail
    }
  };

  const toggleCodeExpand = (codeId: string) => {
    if (expandedCodeId === codeId) {
      setExpandedCodeId(null);
    } else {
      setExpandedCodeId(codeId);
      fetchCodeDetail(codeId);
    }
  };

  const getDiscountStatusColor = (status: string): 'warning' | 'success' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'disabled': return 'info';
      default: return 'default';
    }
  };

  const getDiscountStatusLabel = (status: string): string => {
    return status === 'disabled' ? 'Paused' : status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getActionColor = (action: string): string => {
    switch (action) {
      case 'approved': case 'enabled': return '#2e7d32';
      case 'rejected': case 'deleted': return '#d32f2f';
      case 'paused': return '#ed6c02';
      default: return '#757575';
    }
  };

  const handleViewProfile = (profile: PendingProfile) => {
    setSelectedProfile(profile);
    setDialogOpen(true);
    setExpandedCodeId(null);
    setCodeDetails({});
    initFeeOverrideForm(profile);
    fetchDiscountActivity(profile.id);
    fetchProviderDiscountCodes(profile.id);
    logActivity('view_profile', profile.id);
  };

  const handleApprove = (profile: PendingProfile) => {
    setSelectedProfile(profile);
    setActionType('approve');
    setApprovalMessage('');
  };

  const handleApproveConfirm = async () => {
    if (!selectedProfile) return;
    await processApproval(selectedProfile.id, 'approved', undefined, approvalMessage || undefined);
    setApprovalMessage('');
    setActionType(null);
  };

  const handleReject = (profile: PendingProfile) => {
    setSelectedProfile(profile);
    setActionType('reject');
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    if (!selectedProfile || !rejectionReason.trim()) return;
    
    await processApproval(selectedProfile.id, 'rejected', rejectionReason);
    setRejectionReason('');
    setActionType(null);
  };

  const processApproval = async (profileId: string, status: 'approved' | 'rejected', reason?: string, message?: string) => {
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/profiles/${profileId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          status,
          reason,
          message,
        }),
      });

      if (response.ok) {
        // Surface backend warnings (e.g. provider approved but not geocoded,
        // meaning they will not appear in app search)
        try {
          const result = await response.json();
          if (Array.isArray(result.warnings) && result.warnings.length > 0) {
            window.alert(`Warning:\n\n${result.warnings.join('\n\n')}`);
          }
        } catch {}

        // Remove from pending list
        setProfiles(prev => prev.filter(p => p.id !== profileId));

        // Log activity
        logActivity(status === 'approved' ? 'approve' : 'reject', profileId);

        // Close dialogs
        setDialogOpen(false);
        setActionType(null);
        setSelectedProfile(null);
      }
    } catch (error) {
      console.error('Failed to process approval:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceTypeChange = async (profileId: string, newServiceType: PendingProfile['serviceType']) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/set-service-type/${profileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ serviceType: newServiceType }),
      });

      if (response.ok) {
        // Update local state
        setProfiles(prev => prev.map(p =>
          p.id === profileId ? { ...p, serviceType: newServiceType } : p
        ));
        if (selectedProfile?.id === profileId) {
          setSelectedProfile({ ...selectedProfile, serviceType: newServiceType });
        }
      }
    } catch (error) {
      console.error('Failed to update service type:', error);
    }
  };

  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const handleProfileStatusChange = async (profileId: string, newStatus: 'approved' | 'suspended' | 'rejected', reason?: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/profiles/${profileId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, reason }),
      });

      if (response.ok) {
        fetchProfiles();
        setDialogOpen(false);
        setSelectedProfile(null);
        setSuspendDialogOpen(false);
        setSuspendReason('');
      }
    } catch (error) {
      console.error('Failed to update profile status:', error);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getHoursAgo = (date: Date) => {
    const hours = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
    return hours;
  };

  if (loading && profiles.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Service Type</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell>Submitted</TableCell>
              {statusFilter === 'pending' && <TableCell>Urgency</TableCell>}
              {statusFilter === 'rejected' && <TableCell>Rejection Reason</TableCell>}
              {statusFilter === 'approved' && <TableCell>Services</TableCell>}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((profile) => {
              const serviceInfo = getServiceTypeInfo(profile.serviceType);
              const hoursAgo = getHoursAgo(profile.submittedAt);

              return (
                <TableRow key={profile.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={profile.sitterInfo?.profileImage || profile.groomerInfo?.profileImage || profile.vetInfo?.profileImage || profile.trainerInfo?.profileImage || undefined}
                        sx={{ bgcolor: statusFilter === 'approved' ? 'success.main' : statusFilter === 'rejected' ? 'error.main' : 'primary.main' }}
                      >
                        {profile.personalInfo.firstName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {profile.personalInfo.firstName} {profile.personalInfo.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {profile.personalInfo.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={serviceInfo.label}
                      color={serviceInfo.color as any}
                      size="small"
                      icon={<span>{serviceInfo.icon}</span>}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {profile.personalInfo.country}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip size="small" variant="outlined" label={profile.language === 'de' ? 'Deutsch' : 'English'} color={profile.language === 'de' ? 'info' : 'default'} />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {profile.personalInfo.yearsOfExperience} years
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatDate(profile.submittedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hoursAgo}h ago
                      </Typography>
                    </Box>
                  </TableCell>

                  {statusFilter === 'pending' && (
                    <TableCell>
                      <Chip
                        label={profile.urgency}
                        color={getUrgencyColor(profile.urgency) as any}
                        size="small"
                        icon={profile.urgency === 'urgent' ? <Warning /> : <Schedule />}
                      />
                    </TableCell>
                  )}

                  {statusFilter === 'rejected' && (
                    <TableCell>
                      <Typography variant="body2" color="error" sx={{ maxWidth: 200 }} noWrap>
                        {profile.rejectionReason || 'No reason provided'}
                      </Typography>
                    </TableCell>
                  )}

                  {statusFilter === 'approved' && (
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {profile.services.slice(0, 2).map((s, i) => (
                          <Chip key={i} label={s.name} size="small" variant="outlined" />
                        ))}
                        {profile.services.length > 2 && (
                          <Chip label={`+${profile.services.length - 2}`} size="small" />
                        )}
                      </Box>
                    </TableCell>
                  )}

                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewProfile(profile)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {statusFilter === 'pending' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(profile)}
                              disabled={loading}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReject(profile)}
                              disabled={loading}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {statusFilter === 'rejected' && (
                        <Tooltip title="Re-approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(profile)}
                            disabled={loading}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}

                      {statusFilter === 'approved' && (
                        <Tooltip title="Suspend">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleReject(profile)}
                            disabled={loading}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {profiles.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            {statusFilter === 'pending' ? 'No pending profiles' :
             statusFilter === 'approved' ? 'No approved profiles' :
             'No rejected profiles'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusFilter === 'pending' ? 'All service provider profiles have been reviewed' :
             statusFilter === 'approved' ? 'No service providers have been approved yet' :
             'No service providers have been rejected'}
          </Typography>
        </Box>
      )}

      {/* Profile Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedProfile && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={selectedProfile.sitterInfo?.profileImage || undefined}
                  sx={{ bgcolor: 'primary.main' }}
                >
                  {selectedProfile.personalInfo.firstName.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedProfile.personalInfo.firstName} {selectedProfile.personalInfo.lastName}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 180, mt: 0.5 }}>
                    <Select
                      value={selectedProfile.serviceType}
                      onChange={(e) => handleServiceTypeChange(selectedProfile.id, e.target.value as PendingProfile['serviceType'])}
                    >
                      <MenuItem value="sitter">🐕 Pet Sitter</MenuItem>
                      <MenuItem value="groomer">✂️ Pet Groomer</MenuItem>
                      <MenuItem value="vet">🏥 Veterinarian</MenuItem>
                      <MenuItem value="trainer">🎾 Pet Trainer</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Grid container spacing={3}>
                {/* Profile Photo + About (sitter) */}
                {selectedProfile.serviceType === 'sitter' && selectedProfile.sitterInfo && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" gap={3} alignItems="flex-start">
                          {selectedProfile.sitterInfo.profileImage && (
                            <Box
                              component="img"
                              src={selectedProfile.sitterInfo.profileImage}
                              sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                          )}
                          <Box flex={1}>
                            <Typography variant="h6" gutterBottom>About</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedProfile.sitterInfo.aboutMe || 'No description provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Profile Photo + About (groomer) */}
                {selectedProfile.serviceType === 'groomer' && selectedProfile.groomerInfo && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" gap={3} alignItems="flex-start">
                          {selectedProfile.groomerInfo.profileImage && (
                            <Box
                              component="img"
                              src={selectedProfile.groomerInfo.profileImage}
                              sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                          )}
                          <Box flex={1}>
                            <Typography variant="h6" gutterBottom>
                              {selectedProfile.groomerInfo.businessName || 'About'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedProfile.groomerInfo.aboutMe || 'No description provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Profile Photo + About (vet) */}
                {selectedProfile.serviceType === 'vet' && selectedProfile.vetInfo && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" gap={3} alignItems="flex-start">
                          {selectedProfile.vetInfo.profileImage && (
                            <Box
                              component="img"
                              src={selectedProfile.vetInfo.profileImage}
                              sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                          )}
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {selectedProfile.vetInfo.clinicName || 'About'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedProfile.vetInfo.aboutMe || 'No description provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Profile Photo + About (trainer) */}
                {selectedProfile.serviceType === 'trainer' && selectedProfile.trainerInfo && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" gap={3} alignItems="flex-start">
                          {selectedProfile.trainerInfo.profileImage && (
                            <Box
                              component="img"
                              src={selectedProfile.trainerInfo.profileImage}
                              sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                          )}
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              About
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedProfile.trainerInfo.aboutMe || 'No description provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Personal Information
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Email fontSize="small" />
                        <Typography variant="body2">
                          {selectedProfile.personalInfo.email}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Phone fontSize="small" />
                        <Typography variant="body2">
                          {selectedProfile.personalInfo.phoneNumber}
                        </Typography>
                      </Box>
                      {selectedProfile.personalInfo.dateOfBirth && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Cake fontSize="small" />
                          <Typography variant="body2">
                            {new Date(selectedProfile.personalInfo.dateOfBirth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Typography>
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationOn fontSize="small" />
                        <Typography variant="body2">
                          {selectedProfile.personalInfo.address}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Star fontSize="small" />
                        <Typography variant="body2">
                          {selectedProfile.personalInfo.yearsOfExperience} years experience
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="bold">Country:</Typography>
                          <Typography variant="body2">{selectedProfile.personalInfo.country || 'Not set'}</Typography>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel>Update Country</InputLabel>
                          <Select
                            label="Update Country"
                            value=""
                            onChange={async (e) => {
                              const newCountry = e.target.value as string;
                              if (!newCountry) return;
                              try {
                                const response = await fetch(`${API_BASE_URL}/api/admin/profiles/${selectedProfile.id}/country`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                                  },
                                  body: JSON.stringify({ country: newCountry }),
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  alert(`Updated to ${newCountry} (${data.currency})`);
                                  fetchProfiles();
                                } else {
                                  alert('Failed to update country');
                                }
                              } catch (err) {
                                alert('Error updating country');
                              }
                            }}
                          >
                            <MenuItem value="DE">Germany (EUR)</MenuItem>
                            <MenuItem value="GB">United Kingdom (GBP)</MenuItem>
                            <MenuItem value="FR">France (EUR)</MenuItem>
                            <MenuItem value="ES">Spain (EUR)</MenuItem>
                            <MenuItem value="IT">Italy (EUR)</MenuItem>
                            <MenuItem value="NL">Netherlands (EUR)</MenuItem>
                            <MenuItem value="BE">Belgium (EUR)</MenuItem>
                            <MenuItem value="AT">Austria (EUR)</MenuItem>
                            <MenuItem value="IE">Ireland (EUR)</MenuItem>
                            <MenuItem value="PT">Portugal (EUR)</MenuItem>
                            <MenuItem value="CH">Switzerland (CHF)</MenuItem>
                            <MenuItem value="SE">Sweden (SEK)</MenuItem>
                            <MenuItem value="NO">Norway (NOK)</MenuItem>
                            <MenuItem value="DK">Denmark (DKK)</MenuItem>
                            <MenuItem value="FI">Finland (EUR)</MenuItem>
                            <MenuItem value="GR">Greece (EUR)</MenuItem>
                            <MenuItem value="PL">Poland (PLN)</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Professional / Sitter Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      {selectedProfile.serviceType === 'sitter' && selectedProfile.sitterInfo ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Sitter Details
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <Home fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Home Type</Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                {selectedProfile.sitterInfo.homeType || 'Not specified'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                            <Pets fontSize="small" sx={{ mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Pets Welcome</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.sitterInfo.petsWelcome || []).length > 0 ? (
                                  selectedProfile.sitterInfo.petsWelcome!.map((pet, i) => (
                                    <Chip key={i} label={pet.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2">Not specified</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <VerifiedUser fontSize="small" sx={{ mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Skills & Certifications</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.sitterInfo.skills || []).length > 0 ? (
                                  selectedProfile.sitterInfo.skills!.map((skill, i) => (
                                    <Chip key={i} label={skill.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">None listed</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      ) : selectedProfile.serviceType === 'groomer' && selectedProfile.groomerInfo ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Groomer Details
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <Business fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Grooming Type</Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                {selectedProfile.groomerInfo.groomingType || 'Not specified'}
                              </Typography>
                            </Box>
                          </Box>
                          {selectedProfile.groomerInfo.registeredBusiness && (
                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                              <CheckCircle fontSize="small" color="success" />
                              <Box>
                                <Typography variant="body2" fontWeight="bold">Registered Business</Typography>
                                {selectedProfile.groomerInfo.taxNumber && (
                                  <Typography variant="caption" color="text.secondary">
                                    Tax #: {selectedProfile.groomerInfo.taxNumber}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          {selectedProfile.groomerInfo.professionalTraining && (
                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                              <VerifiedUser fontSize="small" color="primary" />
                              <Box>
                                <Typography variant="body2" fontWeight="bold">Professionally Trained</Typography>
                                {selectedProfile.groomerInfo.trainingDetails && (
                                  <Typography variant="caption" color="text.secondary">
                                    {selectedProfile.groomerInfo.trainingDetails}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          <Divider sx={{ my: 1.5 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">Insurance Coverage</Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5} mb={1.5}>
                            <Chip label="Liability" size="small" color={selectedProfile.groomerInfo.liabilityInsurance ? 'success' : 'default'} variant="outlined" />
                            <Chip label="Pet Injury" size="small" color={selectedProfile.groomerInfo.insuredPetInjury ? 'success' : 'default'} variant="outlined" />
                            <Chip label="Pet Escape" size="small" color={selectedProfile.groomerInfo.insuredPetEscape ? 'success' : 'default'} variant="outlined" />
                            <Chip label="Property Damage" size="small" color={selectedProfile.groomerInfo.insuredPropertyDamage ? 'success' : 'default'} variant="outlined" />
                          </Box>
                          {selectedProfile.groomerInfo.safetyPractices && (
                            <Box mb={1.5}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">Safety Practices</Typography>
                              <Typography variant="body2">{selectedProfile.groomerInfo.safetyPractices}</Typography>
                            </Box>
                          )}
                          <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                            <Pets fontSize="small" sx={{ mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Pets Welcome</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.groomerInfo.petsWelcome || []).length > 0 ? (
                                  selectedProfile.groomerInfo.petsWelcome!.map((pet, i) => (
                                    <Chip key={i} label={pet.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2">Not specified</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <VerifiedUser fontSize="small" sx={{ mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Skills & Certifications</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.groomerInfo.skills || []).length > 0 ? (
                                  selectedProfile.groomerInfo.skills!.map((skill, i) => (
                                    <Chip key={i} label={skill.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">None listed</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      ) : selectedProfile.serviceType === 'vet' && selectedProfile.vetInfo ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Veterinarian Details
                          </Typography>
                          {selectedProfile.vetInfo.clinicName && (
                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                              <Business fontSize="small" />
                              <Box>
                                <Typography variant="caption" color="text.secondary">Clinic / Practice</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {selectedProfile.vetInfo.clinicName}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <VerifiedUser fontSize="small" color={selectedProfile.vetInfo.isLicensed ? 'success' : 'warning'} />
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedProfile.vetInfo.isLicensed ? 'Licensed Veterinarian' : 'Not Licensed'}
                              </Typography>
                              {selectedProfile.vetInfo.isLicensed && selectedProfile.vetInfo.licenseCountry && (
                                <Typography variant="caption" color="text.secondary">
                                  Country: {selectedProfile.vetInfo.licenseCountry}
                                </Typography>
                              )}
                              {selectedProfile.vetInfo.isLicensed && selectedProfile.vetInfo.licenseNumber && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  License #: {selectedProfile.vetInfo.licenseNumber}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                            <Pets fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Animals Treated</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.vetInfo.petsWelcome || []).length > 0 ? (
                                  selectedProfile.vetInfo.petsWelcome!.map((pet, i) => (
                                    <Chip key={i} label={pet.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                            <Star fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Specializations</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.vetInfo.skills || []).length > 0 ? (
                                  selectedProfile.vetInfo.skills!.map((skill, i) => (
                                    <Chip key={i} label={skill.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">None specified</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      ) : selectedProfile.serviceType === 'trainer' && selectedProfile.trainerInfo ? (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Trainer Details
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <VerifiedUser fontSize="small" color={selectedProfile.trainerInfo.isCertified ? 'success' : 'warning'} />
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedProfile.trainerInfo.isCertified ? 'Certified Trainer' : 'Not Certified'}
                              </Typography>
                              {selectedProfile.trainerInfo.isCertified && selectedProfile.trainerInfo.certificationDetails && (
                                <Typography variant="caption" color="text.secondary">
                                  {selectedProfile.trainerInfo.certificationDetails}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                            <Pets fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Animals Trained</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.trainerInfo.petsWelcome || []).length > 0 ? (
                                  selectedProfile.trainerInfo.petsWelcome!.map((pet, i) => (
                                    <Chip key={i} label={pet.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
                            <Star fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Training Methods & Specializations</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {(selectedProfile.trainerInfo.skills || []).length > 0 ? (
                                  selectedProfile.trainerInfo.skills!.map((skill, i) => (
                                    <Chip key={i} label={skill.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">None specified</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Professional Information
                          </Typography>
                          {selectedProfile.professionalInfo.veterinarianLicense && (
                            <Box mb={1}>
                              <Typography variant="body2" fontWeight="bold">
                                Veterinary License:
                              </Typography>
                              <Typography variant="body2">
                                {selectedProfile.professionalInfo.veterinarianLicense}
                              </Typography>
                              <Typography variant="body2">
                                #{selectedProfile.professionalInfo.veterinarianNumber}
                              </Typography>
                            </Box>
                          )}
                          {selectedProfile.professionalInfo.qualification && (
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                Qualifications:
                              </Typography>
                              <Typography variant="body2">
                                {selectedProfile.professionalInfo.qualification}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                      {selectedProfile.spokenLanguages && selectedProfile.spokenLanguages.length > 0 && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <Language fontSize="small" sx={{ mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Languages Spoken</Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {selectedProfile.spokenLanguages.map((lang, i) => (
                                  <Chip key={i} label={lang} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Business Photos (groomer) */}
                {selectedProfile.serviceType === 'groomer' && selectedProfile.groomerInfo?.businessImages && selectedProfile.groomerInfo.businessImages.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <ImageIcon fontSize="small" />
                          <Typography variant="h6">
                            Business Photos ({selectedProfile.groomerInfo.businessImages.length})
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {selectedProfile.groomerInfo.businessImages.map((img, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={img}
                              sx={{
                                width: 200,
                                height: 150,
                                borderRadius: 2,
                                objectFit: 'cover',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Clinic Photos (vet) */}
                {selectedProfile.serviceType === 'vet' && selectedProfile.vetInfo?.clinicImages && selectedProfile.vetInfo.clinicImages.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <ImageIcon fontSize="small" />
                          <Typography variant="h6">
                            Clinic Photos ({selectedProfile.vetInfo.clinicImages.length})
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {selectedProfile.vetInfo.clinicImages.map((img, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={img}
                              sx={{
                                width: 200,
                                height: 150,
                                objectFit: 'cover',
                                borderRadius: 2,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                              }}
                              onClick={() => window.open(img, '_blank')}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Facility Photos (trainer) */}
                {selectedProfile.serviceType === 'trainer' && selectedProfile.trainerInfo?.facilityImages && selectedProfile.trainerInfo.facilityImages.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <ImageIcon fontSize="small" />
                          <Typography variant="h6">
                            Training Facility Photos ({selectedProfile.trainerInfo.facilityImages.length})
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {selectedProfile.trainerInfo.facilityImages.map((img, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={img}
                              sx={{
                                width: 200,
                                height: 150,
                                objectFit: 'cover',
                                borderRadius: 2,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.8 },
                              }}
                              onClick={() => window.open(img, '_blank')}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Insurance Proof (groomer) */}
                {selectedProfile.serviceType === 'groomer' && selectedProfile.groomerInfo?.insuranceProof && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <VerifiedUser fontSize="small" />
                          <Typography variant="h6">Insurance Proof</Typography>
                        </Box>
                        <Box
                          component="img"
                          src={selectedProfile.groomerInfo.insuranceProof}
                          sx={{
                            maxWidth: 400,
                            maxHeight: 300,
                            borderRadius: 2,
                            objectFit: 'contain',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Qualification Proof (vet) */}
                {selectedProfile.serviceType === 'vet' && selectedProfile.vetInfo?.qualificationProof && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <VerifiedUser fontSize="small" />
                          <Typography variant="h6">Qualification Proof</Typography>
                        </Box>
                        <Box
                          component="img"
                          src={selectedProfile.vetInfo.qualificationProof}
                          sx={{
                            maxWidth: 400,
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 2,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 },
                          }}
                          onClick={() => window.open(selectedProfile.vetInfo!.qualificationProof!, '_blank')}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Certification Proof (trainer) */}
                {selectedProfile.serviceType === 'trainer' && selectedProfile.trainerInfo?.certificationProof && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <VerifiedUser fontSize="small" />
                          <Typography variant="h6">Certification Proof</Typography>
                        </Box>
                        <Box
                          component="img"
                          src={selectedProfile.trainerInfo.certificationProof}
                          sx={{
                            maxWidth: 400,
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 2,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 },
                          }}
                          onClick={() => window.open(selectedProfile.trainerInfo!.certificationProof!, '_blank')}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Home Photos (sitter) */}
                {selectedProfile.serviceType === 'sitter' && selectedProfile.sitterInfo?.homeImages && selectedProfile.sitterInfo.homeImages.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <ImageIcon fontSize="small" />
                          <Typography variant="h6">
                            Home Photos ({selectedProfile.sitterInfo.homeImages.length})
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {selectedProfile.sitterInfo.homeImages.map((img, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={img}
                              sx={{
                                width: 200,
                                height: 150,
                                borderRadius: 2,
                                objectFit: 'cover',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Services */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Services Offered ({selectedProfile.services.length})
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedProfile.services.map((service, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper sx={{ p: 2 }} variant="outlined">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {service.name}
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {service.currency ? `${service.price} ${service.currency}` : `$${service.price}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {service.pricingType.replace('_', ' ')}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Application Lifecycle */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <History color="action" />
                        <Typography variant="h6">Application History</Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                            <Typography variant="h4" color="primary.main">
                              {selectedProfile.timesApplied}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Times Applied
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                            <Typography variant="h4" color="error.main">
                              {selectedProfile.timesRejected}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Times Rejected
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                            <Typography variant="h4" color="warning.main">
                              {selectedProfile.timesSuspended}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Times Suspended
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                            <Typography variant="h4" color="success.main">
                              {selectedProfile.timesReactivated}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Times Reactivated
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Status History Timeline */}
                {selectedProfile.statusHistory && selectedProfile.statusHistory.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <History color="action" />
                          <Typography variant="h6">Status History</Typography>
                        </Box>
                        <Box>
                          {[...selectedProfile.statusHistory].reverse().map((entry, idx) => {
                            const actionColors: Record<string, string> = {
                              submitted: '#1976d2',
                              approved: '#2e7d32',
                              rejected: '#d32f2f',
                              suspended: '#ed6c02',
                              reactivated: '#2e7d32',
                            };
                            const actionLabels: Record<string, string> = {
                              submitted: 'Application Submitted',
                              approved: 'Approved',
                              rejected: 'Rejected',
                              suspended: 'Suspended',
                              reactivated: 'Reactivated',
                            };
                            const color = actionColors[entry.action] || '#666';
                            return (
                              <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, position: 'relative' }}>
                                {/* Timeline line */}
                                {idx < selectedProfile.statusHistory.length - 1 && (
                                  <Box sx={{
                                    position: 'absolute',
                                    left: 11,
                                    top: 24,
                                    bottom: -16,
                                    width: 2,
                                    bgcolor: 'divider',
                                  }} />
                                )}
                                {/* Timeline dot */}
                                <Box sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  bgcolor: color,
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mt: '2px',
                                }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'white' }} />
                                </Box>
                                {/* Content */}
                                <Box flex={1}>
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="subtitle2" sx={{ color, fontWeight: 'bold' }}>
                                      {actionLabels[entry.action] || entry.action}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Intl.DateTimeFormat('en-US', {
                                        month: 'short', day: 'numeric', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                      }).format(new Date(entry.timestamp))}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    by {entry.performedBy?.name || 'Unknown'}
                                    {entry.performedBy?.email ? ` (${entry.performedBy.email})` : ''}
                                  </Typography>
                                  {entry.reason && (
                                    <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: 'grey.50' }}>
                                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                        "{entry.reason}"
                                      </Typography>
                                    </Paper>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {/* Platform Fee Override */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Star fontSize="small" /> Platform Fee
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    {selectedProfile.platformFeeOverridePercent != null && (
                      <Alert
                        severity={
                          selectedProfile.feeExemptUntil && new Date(selectedProfile.feeExemptUntil) < new Date()
                            ? 'warning'
                            : 'info'
                        }
                        sx={{ mb: 2 }}
                      >
                        {selectedProfile.feeExemptUntil && new Date(selectedProfile.feeExemptUntil) < new Date()
                          ? `Fee override expired on ${new Date(selectedProfile.feeExemptUntil).toLocaleDateString()}. Default 17% fee is now active.`
                          : `Custom fee: ${selectedProfile.platformFeeOverridePercent}%${selectedProfile.feeExemptUntil ? ` until ${new Date(selectedProfile.feeExemptUntil).toLocaleDateString()}` : ' (no expiry)'}${selectedProfile.feeOverrideSetBy ? ` — set by ${selectedProfile.feeOverrideSetBy}` : ''}`
                        }
                      </Alert>
                    )}
                    <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
                      <TextField
                        label="Fee Override %"
                        type="number"
                        size="small"
                        value={feeOverridePercent}
                        onChange={(e) => setFeeOverridePercent(e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        helperText="Default is 17%. Set to 0 for no fee."
                        sx={{ width: 180 }}
                      />
                      <TextField
                        label="Exempt Until"
                        type="date"
                        size="small"
                        value={feeExemptUntil}
                        onChange={(e) => setFeeExemptUntil(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText="Leave empty for indefinite"
                        sx={{ width: 200 }}
                      />
                      <Box display="flex" gap={1} sx={{ mt: 0.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={saveFeeOverride}
                          disabled={feeSaving || feeOverridePercent === ''}
                        >
                          {feeSaving ? 'Saving...' : 'Save Fee Override'}
                        </Button>
                        {selectedProfile.platformFeeOverridePercent != null && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={resetFeeOverride}
                            disabled={feeSaving}
                          >
                            Reset to Default
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Discount Codes */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOffer fontSize="small" /> Discount Codes
                </Typography>
                {discountCodesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : providerDiscountCodes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No discount codes for this provider.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ width: 32 }} />
                          <TableCell><strong>Code</strong></TableCell>
                          <TableCell><strong>Discount</strong></TableCell>
                          <TableCell><strong>Uses</strong></TableCell>
                          <TableCell><strong>Expires</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Created</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {providerDiscountCodes.map((dc: any) => (
                          <React.Fragment key={dc._id}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedCodeId === dc._id ? 'none' : undefined } }}
                              onClick={() => toggleCodeExpand(dc._id)}
                            >
                              <TableCell sx={{ pr: 0 }}>
                                <IconButton size="small">
                                  {expandedCodeId === dc._id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                  {dc.code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{dc.discountPercent}%</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {dc.currentUses}{dc.maxUses != null ? ` / ${dc.maxUses}` : ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {dc.expiresAt ? new Date(dc.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getDiscountStatusLabel(dc.status)}
                                  color={getDiscountStatusColor(dc.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(dc.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </Typography>
                              </TableCell>
                            </TableRow>

                            {/* Expanded Detail */}
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedCodeId === dc._id ? undefined : 'none' }}>
                                <Collapse in={expandedCodeId === dc._id} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 1 }}>
                                    {!codeDetails[dc._id] ? (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={20} />
                                      </Box>
                                    ) : (
                                      <Grid container spacing={2}>
                                        {/* Code Details */}
                                        <Grid item xs={12} md={5}>
                                          <Paper variant="outlined" sx={{ p: 2 }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>Details</Typography>
                                            <Grid container spacing={1}>
                                              <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Applies To</Typography>
                                                <Typography variant="body2">
                                                  {dc.appliesTo === 'all_services' ? 'All Services' : `Specific (${dc.serviceIds?.length || 0})`}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Per Customer Limit</Typography>
                                                <Typography variant="body2">{dc.maxUsesPerCustomer || 1}</Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Total Uses</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                  {dc.currentUses}{dc.maxUses != null ? ` / ${dc.maxUses}` : ' (unlimited)'}
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Expires</Typography>
                                                <Typography variant="body2">
                                                  {dc.expiresAt ? new Date(dc.expiresAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'}
                                                </Typography>
                                              </Grid>
                                              {dc.statusReason && (
                                                <Grid item xs={12}>
                                                  <Typography variant="caption" color="text.secondary">Rejection Reason</Typography>
                                                  <Typography variant="body2" color="error.main" sx={{ fontStyle: 'italic' }}>
                                                    {dc.statusReason}
                                                  </Typography>
                                                </Grid>
                                              )}
                                            </Grid>

                                            {/* Usage */}
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Person fontSize="small" /> Customer Usage
                                            </Typography>
                                            {codeDetails[dc._id]?.usages?.length > 0 ? (
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow>
                                                    <TableCell><strong>Customer</strong></TableCell>
                                                    <TableCell><strong>Date</strong></TableCell>
                                                    <TableCell align="right"><strong>Amount</strong></TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {codeDetails[dc._id].usages.map((u: any, i: number) => (
                                                    <TableRow key={i}>
                                                      <TableCell>{u.customerName || 'Unknown'}</TableCell>
                                                      <TableCell>{new Date(u.usedAt).toLocaleDateString()}</TableCell>
                                                      <TableCell align="right">{((u.originalAmount || 0) / 100).toFixed(2)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                {dc.currentUses > 0 ? `Used ${dc.currentUses} time(s) — detailed tracking coming soon.` : 'No usage yet.'}
                                              </Typography>
                                            )}
                                          </Paper>
                                        </Grid>

                                        {/* Activity History */}
                                        <Grid item xs={12} md={7}>
                                          <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <History fontSize="small" /> Activity History
                                            </Typography>
                                            {codeDetails[dc._id]?.activity?.length > 0 ? (
                                              <Box>
                                                {codeDetails[dc._id].activity.map((item: any, index: number) => (
                                                  <Box
                                                    key={item._id}
                                                    sx={{ display: 'flex', gap: 1.5, mb: index < codeDetails[dc._id].activity.length - 1 ? 2 : 0 }}
                                                  >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 14 }}>
                                                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getActionColor(item.action), mt: 0.5, flexShrink: 0 }} />
                                                      {index < codeDetails[dc._id].activity.length - 1 && (
                                                        <Box sx={{ width: 2, flex: 1, bgcolor: '#e0e0e0', mt: 0.5 }} />
                                                      )}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                        <Chip
                                                          label={item.action.charAt(0).toUpperCase() + item.action.slice(1)}
                                                          size="small"
                                                          sx={{ bgcolor: getActionColor(item.action), color: '#fff', fontWeight: 'bold', fontSize: '0.65rem', height: 20 }}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                          {new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                                        </Typography>
                                                      </Box>
                                                      <Typography variant="body2" color="text.secondary">
                                                        {item.performedByName ? <>by <strong>{item.performedByName}</strong></> : 'by System'}
                                                      </Typography>
                                                      {item.reason && (
                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', mt: 0.25 }}>
                                                          Reason: {item.reason}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  </Box>
                                                ))}
                                              </Box>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                No activity recorded yet.
                                              </Typography>
                                            )}
                                          </Paper>
                                        </Grid>
                                      </Grid>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Close
              </Button>
              <Button
                variant="outlined"
                onClick={openEditDialog}
              >
                Edit Profile
              </Button>

              {statusFilter === 'pending' && (
                <>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleReject(selectedProfile)}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApprove(selectedProfile)}
                  >
                    Approve
                  </Button>
                </>
              )}

              {statusFilter === 'approved' && (
                <>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Pause />}
                    onClick={() => setSuspendDialogOpen(true)}
                  >
                    Pause Services
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Block />}
                    onClick={() => handleReject(selectedProfile)}
                  >
                    Delist Provider
                  </Button>
                </>
              )}

              {statusFilter === 'rejected' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => handleApprove(selectedProfile)}
                >
                  Re-approve
                </Button>
              )}

              {statusFilter === 'suspended' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrow />}
                    onClick={() => handleApprove(selectedProfile)}
                  >
                    Reactivate
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Block />}
                    onClick={() => handleReject(selectedProfile)}
                  >
                    Delist Provider
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Approval Message Dialog */}
      <Dialog
        open={actionType === 'approve'}
        onClose={() => setActionType(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Profile</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Optionally include a message for the applicant. This will be sent in the approval email notification.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={approvalMessage}
            onChange={(e) => setApprovalMessage(e.target.value)}
            placeholder="e.g. Welcome to Pawtel! Please make sure to complete your availability schedule..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionType(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            Approve Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog
        open={actionType === 'reject'}
        onClose={() => setActionType(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Profile</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for rejecting this profile. This will be sent to the applicant.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide specific feedback on what needs to be improved..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionType(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={!rejectionReason.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Cancel />}
          >
            Reject Profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Reason Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => { setSuspendDialogOpen(false); setSuspendReason(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pause Services</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for pausing this provider's services. This will be sent to the provider via email and shown in the app.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={suspendReason}
            onChange={(e: any) => setSuspendReason(e.target.value)}
            placeholder="Provide a reason for pausing this provider's services..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuspendDialogOpen(false); setSuspendReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => selectedProfile && handleProfileStatusChange(selectedProfile.id, 'suspended', suspendReason)}
            disabled={!suspendReason.trim()}
            startIcon={<Pause />}
          >
            Pause Services
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => !editSaving && setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Provider Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Personal Details</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="First Name" size="small" value={editForm.firstName}
                onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Last Name" size="small" value={editForm.lastName}
                onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Email" size="small" value={editForm.email}
                onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Phone" size="small" value={editForm.phone}
                onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Street" size="small" value={editForm.street}
                onChange={e => setEditForm(f => ({ ...f, street: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="City" size="small" value={editForm.city}
                onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="State/Region" size="small" value={editForm.state}
                onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Zip Code" size="small" value={editForm.zipCode}
                onChange={e => setEditForm(f => ({ ...f, zipCode: e.target.value }))} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>Services</Typography>
            </Grid>
            {editForm.services.map((service, index) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField fullWidth label="Service Name" size="small" value={service.name}
                        onChange={e => {
                          const updated = [...editForm.services];
                          updated[index] = { ...updated[index], name: e.target.value };
                          setEditForm(f => ({ ...f, services: updated }));
                        }} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="Price" size="small" type="number" value={service.price}
                        onChange={e => {
                          const updated = [...editForm.services];
                          updated[index] = { ...updated[index], price: Number(e.target.value) };
                          setEditForm(f => ({ ...f, services: updated }));
                        }} />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField fullWidth label="Currency" size="small" value={service.currency}
                        onChange={e => {
                          const updated = [...editForm.services];
                          updated[index] = { ...updated[index], currency: e.target.value };
                          setEditForm(f => ({ ...f, services: updated }));
                        }} />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField fullWidth label="Pricing Type" size="small" value={service.pricingType}
                        onChange={e => {
                          const updated = [...editForm.services];
                          updated[index] = { ...updated[index], pricingType: e.target.value };
                          setEditForm(f => ({ ...f, services: updated }));
                        }} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveEditDetails} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileApprovalList;